/**
 * EventResponses — admin view of a single event's pre-orders
 * (/events/:id/responses). Shows the Kitchen Summary (total qty per item for
 * prep), top-line counts, a CSV export, and every individual submission.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { subscribeEvent, subscribeOrders, backfillClaims } from './eventsCloud';
import {
  aggregateItems, summarize, ordersToCsv, downloadCsv, formatDateTime, toMs, STATUS_META,
  ordersRevenue, formatMoney, sortItemsBy, prepListText, freeTextOrders, aggregateFreeText,
} from './eventUtils';
import { fieldIsInput } from './fieldTypes';
import ShareModal from './components/ShareModal';
import EventTimeline from './components/EventTimeline';
import OrderEditModal from './components/OrderEditModal';
import { t } from './strings';
import './Events.css';

export default function EventResponses() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(undefined); // undefined=loading, null=missing
  const [orders, setOrders] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [openHist, setOpenHist] = useState({});
  const [prepSort, setPrepSort] = useState(() => {
    try { return localStorage.getItem('ev_prep_sort') || 'name'; } catch { return 'name'; }
  });
  const [copiedPrep, setCopiedPrep] = useState(false);
  const setSort = (m) => { setPrepSort(m); try { localStorage.setItem('ev_prep_sort', m); } catch { /* storage off */ } };
  const toggleHist = (oid) => setOpenHist((m) => ({ ...m, [oid]: !m[oid] }));

  useEffect(() => {
    const u1 = subscribeEvent(id, setEvent, () => setEvent(null));
    const u2 = subscribeOrders(id, setOrders);
    return () => { u1(); u2(); };
  }, [id]);

  // Backfill phone→order claims once so older orders become findable by customers
  // (cross-device "find my order"). Idempotent + best-effort; staff-only view.
  const backfilledRef = useRef(false);
  useEffect(() => {
    if (backfilledRef.current || orders.length === 0) return;
    backfilledRef.current = true;
    backfillClaims(id, orders).catch(() => {});
  }, [id, orders]);

  const sorted = useMemo(
    () => [...orders].sort((a, b) => (toMs(b.submittedAt) || 0) - (toMs(a.submittedAt) || 0)),
    [orders],
  );
  const agg = useMemo(() => aggregateItems(orders), [orders]);              // structured menu items
  const freeAgg = useMemo(() => aggregateFreeText(orders), [orders]);       // parsed from free text
  // Prefer real counted items; otherwise fall back to the parsed free-text tallies.
  const summaryItems = useMemo(() => (agg.length > 0 ? agg : freeAgg), [agg, freeAgg]);
  const isParsed = agg.length === 0 && freeAgg.length > 0;
  const aggSorted = useMemo(() => sortItemsBy(summaryItems, prepSort), [summaryItems, prepSort]);
  const grandTotal = useMemo(() => summaryItems.reduce((s, r) => s + (Number(r.totalQty) || 0), 0), [summaryItems]);
  const freeText = useMemo(() => freeTextOrders(orders), [orders]); // raw free-text orders, A→Z (for reference)
  const { totalOrders, totalItems } = summarize(orders);
  const revenue = useMemo(() => ordersRevenue(orders), [orders]);
  const maxQty = summaryItems.length ? summaryItems[0].totalQty : 0;

  // Copy the item summary (counted or parsed-from-free-text) as a prep checklist.
  const copyPrep = async () => {
    try {
      await navigator.clipboard.writeText(prepListText(event, summaryItems, prepSort));
      setCopiedPrep(true);
      setTimeout(() => setCopiedPrep(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  const fields = useMemo(
    () => (event && event.fields ? event.fields.filter((f) => fieldIsInput(f.type)) : []),
    [event],
  );

  const exportCsv = () => {
    if (!event) return;
    const safe = (event.title || 'event').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    downloadCsv(`${safe}-orders.csv`, ordersToCsv(event, sorted));
  };

  if (event === undefined) {
    return <div className="ev"><div className="ev__inner"><div className="ev__loading">{t('loading')}</div></div></div>;
  }
  if (event === null) {
    return (
      <div className="ev"><div className="ev__inner">
        <button className="ev-linkbtn ev-linkbtn--back" onClick={() => navigate('/events')}>← {t('back')}</button>
        <div className="ev__empty"><div className="ev__empty-icon">🔍</div><p>{t('notFoundTitle')}</p></div>
      </div></div>
    );
  }

  const meta = STATUS_META[event.status] || STATUS_META.draft;

  return (
    <div className="ev">
      <div className="ev__inner">
        <header className="ev__hero">
          <div>
            <button className="ev-linkbtn ev-linkbtn--back" onClick={() => navigate('/events')}>← {t('back')}</button>
            <h1 className="ev__title"><span className="ev__title-grad">{event.title || 'Untitled event'}</span></h1>
            <p className="ev__sub">
              <span className="ev__chip" style={{ color: meta.color, background: `${meta.color}22` }}>{meta.icon} {meta.label}</span>
            </p>
          </div>
          <div className="ev__actions-col">
            <button className="ev__btn ev__btn--sm" onClick={() => setShowShare(true)}>🔗 {t('share')}</button>
            <button className="ev__btn ev__btn--sm" onClick={() => navigate(`/events/${id}/edit`)}>✎ {t('edit')}</button>
            <button className="ev__btn ev__btn--sm" onClick={exportCsv} disabled={!orders.length}>⬇ {t('exportCsv')}</button>
          </div>
        </header>

        {/* Top-line counts */}
        <div className="ev__cards">
          <div className="ev__statcard"><span className="ev__stat-num">{totalOrders}</span><span className="ev__stat-lbl">{t('totalOrders')}</span></div>
          <div className="ev__statcard"><span className="ev__stat-num">{totalItems}</span><span className="ev__stat-lbl">{t('totalItems')}</span></div>
          {revenue > 0 && (
            <div className="ev__statcard"><span className="ev__stat-num">{formatMoney(revenue)}</span><span className="ev__stat-lbl">{t('revenue')}</span></div>
          )}
        </div>

        {/* Kitchen Summary */}
        {summaryItems.length > 0 && (
          <section className="ev-panel">
            <div className="ev-prep__head">
              <h2 className="ev-panel__title" style={{ margin: 0 }}>🍳 {t('kitchenSummary')}</h2>
              <div className="ev-prep__tools">
                <div className="ev-prep__sort" role="group" aria-label={t('sortLabel')}>
                  <button type="button" className={`ev-prep__sortbtn${prepSort === 'name' ? ' ev-prep__sortbtn--on' : ''}`} onClick={() => setSort('name')}>{t('sortAZ')}</button>
                  <button type="button" className={`ev-prep__sortbtn${prepSort === 'qty' ? ' ev-prep__sortbtn--on' : ''}`} onClick={() => setSort('qty')}>{t('sortBusiest')}</button>
                </div>
                <button type="button" className="ev__btn ev__btn--sm" onClick={copyPrep}>{copiedPrep ? t('copiedPrep') : t('copyPrep')}</button>
              </div>
            </div>
            <p className="ev-prep__meta">
              {grandTotal} {t('items')} · {summaryItems.length} {t('prepTypes')}
              {isParsed ? <span className="ev-prep__note"> · {t('prepParsedNote')}</span> : null}
            </p>
            <div className="ev-agg">
              {aggSorted.map((row) => (
                <div key={row.id || row.name} className="ev-agg__row">
                  <span className="ev-agg__name">{row.name}</span>
                  <div className="ev-agg__bar-wrap">
                    <div className="ev-agg__bar" style={{ width: `${maxQty ? (row.totalQty / maxQty) * 100 : 0}%` }} />
                  </div>
                  <span className="ev-agg__qty">×{row.totalQty}</span>
                </div>
              ))}
              <div className="ev-agg__row ev-agg__total">
                <span className="ev-agg__name">{t('prepTotalRow')}</span>
                <div className="ev-agg__bar-wrap" />
                <span className="ev-agg__qty">×{grandTotal}</span>
              </div>
            </div>
          </section>
        )}

        {/* Raw free-text orders, for reference so staff can verify the auto-count above */}
        {agg.length === 0 && freeText.length > 0 && (
          <section className="ev-panel">
            <h2 className="ev-panel__title">{t('ordersAsTyped')}</h2>
            <p className="ev-prep__meta">{freeText.length} {t('orders')}</p>
            <div className="ev-freelist">
              {freeText.map((o) => (
                <div key={o.id} className="ev-freelist__row">
                  <span className="ev-freelist__name">{o.respondentName || t('noName')}</span>
                  <span className="ev-freelist__order">{o.orderText}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Individual responses */}
        <section className="ev-panel">
          <h2 className="ev-panel__title">{t('responses')} ({totalOrders})</h2>
          {sorted.length === 0 ? (
            <p className="ev-empty-sm">{t('noResponses')}</p>
          ) : (
            <div className="ev-responses">
              {sorted.map((o) => (
                <div key={o.id} className="ev-response">
                  <div className="ev-response__head">
                    <span className="ev-response__name">{o.respondentName || t('noName')}</span>
                    <span className="ev-response__head-right">
                      <span className="ev-response__time">{formatDateTime(o.submittedAt)}</span>
                      <button type="button" className="ev-response__edit" onClick={() => setEditOrder(o)}>✎ {t('edit')}</button>
                    </span>
                  </div>
                  {(o.respondentPhone || o.respondentEmail) && (
                    <div className="ev-response__contact">
                      {o.respondentPhone ? <span>📞 {o.respondentPhone}</span> : null}
                      {o.respondentEmail ? <span>📧 {o.respondentEmail}</span> : null}
                    </div>
                  )}
                  {(o.items || []).length > 0 && (
                    <div className="ev-response__items">
                      {o.items.map((it, i) => (
                        <span key={i} className="ev-tag">{it.qty}× {it.name}</span>
                      ))}
                    </div>
                  )}
                  {o.orderText ? <div className="ev-response__note">🍽 {o.orderText}</div> : null}
                  {fields.length > 0 && (
                    <div className="ev-response__answers">
                      {fields.map((f) => {
                        const v = o.answers ? o.answers[f.id] : undefined;
                        if (v == null || (Array.isArray(v) && v.length === 0) || v === '') return null;
                        return (
                          <div key={f.id} className="ev-response__answer">
                            <span className="ev-response__q">{f.label}:</span>{' '}
                            <span>{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {o.instructions ? <div className="ev-response__note">📝 {o.instructions}</div> : null}
                  {(o.history?.length || o.submittedAt) ? (
                    <div className="ev-response__hist">
                      <button type="button" className="ev-linkbtn" onClick={() => toggleHist(o.id)}>
                        🕘 {openHist[o.id] ? t('hide') : t('orderHistoryLabel')}{o.history?.length ? ` (${o.history.length})` : ''}
                      </button>
                      {openHist[o.id] && <EventTimeline history={o.history} placedAt={o.submittedAt} />}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Activity timeline (audit trail) */}
        <section className="ev-panel">
          <h2 className="ev-panel__title">🕘 {t('activity')}</h2>
          <EventTimeline history={event.history} />
        </section>
      </div>
      <ShareModal open={showShare} onClose={() => setShowShare(false)} eventId={id} title={event.title} />
      {editOrder && (
        <OrderEditModal event={event} order={editOrder} onClose={() => setEditOrder(null)} />
      )}
    </div>
  );
}
